import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { SortingRule } from "react-table";
import ButtonComponent from "../../../components/ButtonComponent";
import TableComponent from "../../../components/TableComponent";
import Routes from "../../../helpers/Routes";
import Toast from "../../../helpers/Toast";
import { Utilities } from "../../../helpers/Utilities";
import { EquipmentDTO } from "../../../models/Equipment/EquipmentDTO";
import { Parameter } from "../../../models/Parameter";
import { EquipmentSearchFilters } from "../../../pages/workshop/EquipmentSearchFilters";
import { EquipmentService } from "../../../services/EquipmentService";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";

export default function AssistanceSearchCreateA() {
    //Pagina
    const navigate = useNavigate();
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const equipmentService = new EquipmentService();
    const [showTable, setShowTable] = useState(false);

    //Filtros pesquisa
    const [serialNumber, setSerialNumber] = useState<string>("");
    const [clientVatNumber, setClientVatNumber] = useState<string>("");
    const [freeSearch, setFreeSearch] = useState<string>("");
    const [carPlate, setCarPlate] = useState<string>("");


    //Tabela
    const [data, setData] = useState<EquipmentDTO[]>([]);
    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const pageSizes: number[] = [5, 15, 30];
    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));

    //módulos ativos
    const { availableModules } = useAvailableModulesContext();
    const [isActiveCarPlate, setIsActiveCarPlate] = useState<boolean>(false);


    useEffect(() => {
        if (availableModules.includes("IsActiveCarPlate")) {
            setIsActiveCarPlate(true);
        }
    }, [availableModules]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Código Equipamento",
                accessor: "erpId",
                width: 5,
            },
            {
                Header: "Nome Equipamento",
                accessor: "name",
                width: 20,
            },
            ...(isActiveCarPlate ? [{
                Header: "Matrícula",
                accessor: "carPlate",
                width: 10,
            }] : []),
            {
                Header: "Nº Série",
                accessor: "serialNumber",
                width: 10,
            },
            {
                Header: "Marca",
                accessor: "brand",
                width: 10,
            },
            {
                Header: "Modelo",
                accessor: "model",
                width: 10,
            },
            {
                Header: "Código Cliente",
                accessor: "client.erpId",
                width: 10,
            },
            {
                Header: "Nome Cliente",
                accessor: "client.name",
                width: 20,
            },
            {
                Header: "NIF",
                accessor: "client.vatNumber",
                width: 5,
            },
            {
                Header: "Opções",
                accessor: "",
                width: 10,
                Cell: (props: any) => {
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                alignContent: "center",
                                flexDirection: "column",
                            }}
                        >
                            <ButtonComponent
                                text={intl.formatMessage({ id: "AssistanceSearchCreateA.Select" })}
                                onClick={() => navigate(`${Routes.oficina.create}/${props.cell.row.original.erpId}`)}
                            />
                        </div>
                    );
                },
            },
        ],
        [isActiveCarPlate]
    );

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
    }, [reloadSearch]);

    const onButtonClick = async () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };

    const onRemoveFiltersClick = async () => {
        setSerialNumber("");
        setClientVatNumber("");
        setFreeSearch("");
        setCarPlate("");

        setShowTable(false);
        navigate(`${Routes.oficina.create}`, { replace: true });
        setData([]);
        setReloadSearch(true);
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("freeSearch", freeSearch));
        return parameters;
    };

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        if (!serialNumber && !clientVatNumber && !freeSearch) {
            setShowTable(false);
            return;
        }

        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
            Utilities.AddParamToQueryIfValue("freeSearch", "string", freeSearch),
        ]);
        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        navigate(`${Routes.oficina.create}${searchUrl}`, { replace: true });

        setIsLoadingTable(true);
        setShowTable(true);
        var response = await equipmentService.GetAll(mountSearchParameters(), Utilities.MountSortParameters(sortBy), page, pageSize);

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setIsLoadingTable(false);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        if (response.items.length === 0) {
            setData([]);
            setPageCount(0);
            setIsLoadingTable(false);
            setCurrentSorting(null);
            Toast.Show("info", "Não existe nenhum equipamento com esses dados");
            return;
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setIsLoadingTable(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const intl = useIntl();

    return (
        <>
            <EquipmentSearchFilters
                serialNumber={serialNumber}
                setSerialNumber={setSerialNumber}
                clientVatNumber={clientVatNumber}
                setClientVatNumber={setClientVatNumber}
                freeSearch={freeSearch}
                setFreeSearch={setFreeSearch}
                carPlate={carPlate}
                setCarPlate={setCarPlate}

                onButtonClick={onButtonClick}
                onRemoveFiltersClick={onRemoveFiltersClick}
            />

            {showTable === true && (
                <div className="pageComponent" style={{ width: "100%" }}>
                    <TableComponent
                        goToPage={goToPage}
                        loading={isLoadingTable}
                        pageCount={pageCount}
                        columns={columns}
                        manualPagination={true}
                        fetchData={fetchData}
                        pageSizes={pageSizes}
                        initialPage={page}
                        initialPageSize={pageSize}
                        initialSortBy={initialSortBy}
                        data={data}
                    />
                </div>
            )}
        </>
    );
}
